import React from "react";
import HeroSection from "../components/HeroSection";
import { heroSections } from "../constants/data.ts";
import images from "../constants/images.ts";

const applicationServices = [
  {
    title: "Web Application Development",
    description:
      "We build responsive, secure, and high-performance web applications that deliver seamless user experiences across all modern browsers and devices. Our solutions are optimized for speed, scalability, and long-term business growth.",
    listTitle: "Our Expertise Includes:",
    image: images.applicationDevelopmentServices.webApplicationDevelopment,
    items: [
      "Corporate Websites",
      "Mobile App Development",
      "Business Management Systems",
      "CRM & ERP Solutions",
      "Customer Portals",
      "E-commerce Platforms",
      "SaaS Applications",
      "Admin Dashboards",
      "Progressive Web Applications (PWA)",
    ],
  },
  {
    title: "Mobile App Development",
    description:
      "We develop feature-rich mobile applications for Android and iOS that provide exceptional performance and intuitive user experiences. Our apps are designed to engage users while maintaining security, reliability, and scalability.",
    listTitle: "Our Mobile Solutions Include:",
    image: images.applicationDevelopmentServices.mobileAppDevelopment,
    items: [
      "Android Applications",
      "iOS Applications",
      "Cross-Platform Apps",
      "Enterprise Mobility Solutions",
      "Business Applications",
      "Customer Service Apps",
      "Booking & Reservation Apps",
      "E-commerce Mobile Apps",
    ],
  },
  {
    title: "Custom Software Solutions",
    description:
      "Every business has unique processes and challenges. We create custom software solutions specifically designed around your business goals, improving operational efficiency and automating critical workflows.",
    listTitle: "Solutions We Develop:",
    image: images.applicationDevelopmentServices.customSoftwareSolutions,
    items: [
      "Enterprise Software",
      "Workflow Automation",
      "HR Management Systems",
      "Inventory Management",
      "Education Management Systems",
      "Healthcare Solutions",
      "Finance & Accounting Software",
      "Manufacturing Solutions",
    ],
  },
  {
    title: "UI/UX Design",
    description:
      "A great application starts with an exceptional user experience. Our UI/UX design team creates modern, intuitive, and visually appealing interfaces that improve usability, increase engagement, and enhance customer satisfaction.",
    listTitle: "Design Services Include:",
    image: images.applicationDevelopmentServices.uiuxDesign,
    items: [
      "User Research",
      "Wireframing",
      "Interactive Prototypes",
      "Responsive UI Design",
      "Dashboard Design",
      "Mobile App Design",
      "Website Design",
      "Design System Creation",
    ],
  },
  {
    title: "API Integration",
    description:
      "We integrate your applications with third-party platforms and enterprise systems to automate processes, improve data flow, and create connected digital ecosystems.",
    listTitle: "Integration Services:",
    image: images.applicationDevelopmentServices.apiIntegration,
    items: [
      "Payment Gateway Integration",
      "SMS & Email Services",
      "Authentication Systems",
      "Social Media APIs",
      "ERP & CRM Integration",
      "Cloud Services Integration",
      "Maps & Location APIs",
      "Third-Party Business APIs",
    ],
  },
  {
    title: "Maintenance & Support",
    description:
      "Launching your application is only the beginning. We provide continuous maintenance and technical support to ensure your software remains secure, stable, and up to date.",
    listTitle: "Support Services Include:",
    image: images.applicationDevelopmentServices.maintenanceSupport,
    items: [
      "Application Monitoring",
      "Bug Fixes",
      "Performance Optimization",
      "Security Updates",
      "Feature Enhancements",
      "Version Upgrades",
      "Backup & Recovery",
      "Technical Support",
    ],
  },
];

const processSteps = [
  "Requirement Analysis",
  "Planning & Strategy",
  "UI/UX Design",
  "Application Development",
  "Quality Assurance & Testing",
  "Deployment",
  "Backup & Recovery",
  "Maintenance & Continuous Improvement",
];

const reasons = [
  "Experienced Development Team",
  "Custom-Built Business Solutions",
  "Modern Technologies & Frameworks",
  "Scalable & Secure Architecture",
  "Agile Development Methodology",
  "Transparent Communication",
  "On-Time Project Delivery",
  "Dedicated Post-Launch Support",
  "Cost-Effective Development",
  "Customer-Centric Approach",
];

const industries = [
  "Education",
  "Healthcare",
  "Finance",
  "Retail & E-commerce",
  "Manufacturing",
  "Logistics & Transportation",
  "Real Estate",
  "Hospitality",
  "Travel & Tourism",
  "Human Resources",
  "Government",
  "Startups & Enterprises",
];

const ApplicationDevelopment = () => {
  return (
    <main className="bg-background">
      <HeroSection {...heroSections.applicationDevelopment} />

      <section className="bg-white px-outer py-16 md:py-24">
        <div className="mx-auto w-full max-w-6xl">
          <p className="mb-3 heading-main text-primary">
            Application Development
          </p>
          <h2 className="heading-main mb-5">
            Build Scalable Digital Solutions for Your Business
          </h2>
          <div className="space-y-4">
            <p className="text-body">
              At Westlisoft, we develop modern web and mobile applications that
              help businesses streamline operations, improve customer
              engagement, and accelerate growth. Our applications are designed
              with scalability, security, performance, and user experience at
              their core.
            </p>
            <p className="text-body">
              Whether you need a business application, customer portal,
              enterprise solution, or a custom digital platform, our experienced
              development team transforms your ideas into reliable,
              future-ready software using the latest technologies and industry
              best practices.
            </p>
            <p className="text-body">
              From planning and UI/UX design to development, deployment, and
              ongoing maintenance, we provide end-to-end application development
              services tailored to your unique business requirements.
            </p>
          </div>
        </div>
      </section>

      <section className="px-outer p-16 md:pb-24">
        <div className="mx-auto w-full max-w-6xl">
          <div className="mb-12 max-w-3xl">
            <p className="mb-3 heading-main text-primary">
              Our Application Development Services
            </p>
          </div>

          <div className="space-y-10 md:space-y-14">
            {applicationServices.map((service, index) => {
              const imageFirst = index % 2 === 1;

              return (
                <article
                  key={service.title}
                  className="grid items-center gap-6 rounded-card bg-white p-5 shadow-sm md:grid-cols-2 md:gap-10 lg:p-8"
                >
                  <div
                    className={
                      imageFirst
                        ? "order-2 md:order-2"
                        : "order-2 md:order-1"
                    }
                  >
                    <h3 className="mb-4 font-rubik text-2xl font-bold text-secondary md:text-3xl">
                      {service.title}
                    </h3>
                    <p className="text-body mb-5">{service.description}</p>
                    <h4 className="mb-3 font-rubik text-lg font-semibold text-secondary">
                      {service.listTitle}
                    </h4>
                    <ul className="grid gap-2 text-sm font-light text-gray-700 sm:grid-cols-2">
                      {service.items.map((item) => (
                        <li key={item} className="flex gap-2">
                          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div
                    className={
                      imageFirst
                        ? "order-1 flex h-60 items-center justify-center rounded-card bg-gray-50 p-3 sm:h-72 md:order-1 md:h-[320px] lg:h-[360px]"
                        : "order-1 flex h-60 items-center justify-center rounded-card bg-gray-50 p-3 sm:h-72 md:order-2 md:h-[320px] lg:h-[360px]"
                    }
                  >
                    <img
                      src={service.image}
                      alt={service.title}
                      className="h-full w-full object-contain"
                    />
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-white px-outer py-16 md:py-24">
        <div className="mx-auto w-full max-w-6xl text-center">
          <p className="mb-4 heading-main text-primary">
            Our Development Process
          </p>
          <p className="text-body mx-auto mb-8 max-w-3xl">
            We follow a structured development approach to ensure every project
            is delivered on time, within budget, and meets the highest quality
            standards.
          </p>
          <ol className="mx-auto grid max-w-5xl gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {processSteps.map((step, index) => (
              <li
                key={step}
                className="flex min-h-24 flex-col items-center justify-center rounded-card bg-background px-4 py-5 text-center font-rubik text-sm font-light text-gray-700"
              >
                <span className="mb-3 flex h-9 w-9 items-center justify-center rounded-full bg-primary font-bold text-secondary">
                  {index + 1}
                </span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="px-outer py-16 md:py-24">
        <div className="mx-auto w-full max-w-6xl text-center">
          <h2 className="heading-main mb-8 text-primary">
            Why Choose Westlisoft?
          </h2>
          <ul className="mx-auto grid max-w-5xl gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {reasons.map((reason) => (
              <li
                key={reason}
                className="flex min-h-20 items-center justify-center rounded-card bg-white px-4 py-4 text-center font-rubik text-sm font-light text-gray-700 shadow-sm"
              >
                {reason}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="bg-white px-outer py-16 md:py-24">
        <div className="mx-auto w-full max-w-6xl text-center">
          <p className="mb-4 heading-main text-primary">
            Industries We Serve
          </p>
          <p className="text-body mx-auto mb-8 max-w-3xl">
            We deliver application development solutions across multiple
            industries, including:
          </p>
          <ul className="mx-auto grid max-w-5xl gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {industries.map((industry) => (
              <li
                key={industry}
                className="flex min-h-16 items-center justify-center rounded-card bg-background px-4 py-4 text-center font-rubik text-sm font-light text-gray-700"
              >
                {industry}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="px-outer py-16 md:py-24">
        <div className="mx-auto w-full max-w-5xl text-center">
          <h2 className="heading-main mb-4 text-primary">
            Let's Build Your Next Digital Solution
          </h2>
          <p className="text-body mx-auto max-w-4xl">
            Whether you're looking to develop a new web application, mobile app,
            or custom enterprise software, Westlisoft is your trusted technology
            partner. We combine innovation, technical expertise, and business
            understanding to deliver applications that drive measurable results
            and support your long-term growth.
          </p>
        </div>
      </section>
    </main>
  );
};

export default ApplicationDevelopment;
